import { Session } from "@supabase/auth-helpers-nextjs";
import {
    ExerciseCompletion,
    ProductWithPrice,
    StudyLanguage,
    UserSettings
} from "types";

import { exerciseCategories } from "./constants";
import { getLocalTimestamp } from "./helpers";
import { createClient } from "./supabase/client";

export const supabase = createClient();

export const getActiveProductsWithPrices = async (): Promise<
    ProductWithPrice[]
> => {
    const { data, error } = await supabase
        .from("products")
        .select("*, prices(*)")
        .eq("active", true)
        .eq("prices.active", true)
        .order("metadata->index")
        .order("unit_amount", { foreignTable: "prices" });

    if (error) {
        console.log(error.message);
        throw error;
    }
    // TODO: improve the typing here.
    return (data as any) || [];
};

export const updateOnboardingStatus = async (
    session: Session,
    onboard_completed: boolean
) => {
    const { error } = await supabase
        .from("users")
        .update({
            onboard_completed
        })
        .eq("id", session.user.id);

    if (error) {
        console.log(error.message);
        throw error;
    }
};

export const getOnboardingStatus = async () => {
    console.log("client get onboarding status");
    try {
        const {
            data: { user },
            error: sessionError
        } = await supabase.auth.getUser();

        if (sessionError) {
            console.log(sessionError);
        }

        if (!user) return;

        const { data } = await supabase
            .from("users")
            .select("onboard_completed")
            .eq("id", user.id)
            .maybeSingle();

        return data?.onboard_completed;
    } catch (error) {
        console.error("Coulnd't get user onboarding status:", error);
        return null;
    }
};

export const upsertUserSettings = async ({
    interface_language,
    notifications_enabled,
    current_study_language,
    motivation,
    proficiency,
    goals
}: UserSettings) => {
    const {
        data: { user },
        error: sessionError
    } = await supabase.auth.getUser();

    if (sessionError) {
        console.log(sessionError);
    }

    if (!user) return;

    const payload = {
        user_id: user.id,
        ...(interface_language !== undefined && { interface_language }),
        ...(notifications_enabled !== undefined && { notifications_enabled }),
        ...(current_study_language !== undefined && { current_study_language }),
        ...(motivation !== undefined && { motivation }),
        ...(proficiency !== undefined && { proficiency }),
        ...(goals !== undefined && { goals })
    };
    const { error } = await supabase
        .from("user_settings")
        .upsert(payload, { onConflict: "user_id" });

    if (error) {
        console.log(error);
    }
};

// get the user settings from supabase
export const getUserSettings = async (): Promise<UserSettings | null> => {
    try {
        const { data } = await supabase
            .from("user_settings")
            .select(
                "current_study_language, interface_language, notifications_enabled, motivation, proficiency, goals"
            )
            .maybeSingle();

        return data as UserSettings;
    } catch (error) {
        console.error("Couldn't get user settings client", error);
        return null;
    }
};

export const createClientExerciseCompletion = async ({
    category,
    audio_length_seconds,
    study_language
}: ExerciseCompletion) => {
    // return if no category
    if (!category || !exerciseCategories.includes(category)) return;
    // return if audio length present but not listening category
    if (audio_length_seconds && category !== "listening") return;
    // if listening category but no audio length, return
    if (category === "listening" && !audio_length_seconds) return;

    const {
        data: { user }
    } = await supabase.auth.getUser();

    if (!user) return;

    const completed_at_local = getLocalTimestamp();

    try {
        const { data } = await supabase.from("exercise_completions").insert([
            {
                user_id: user.id,
                category,
                completed_at_local,
                study_language,
                ...(audio_length_seconds && { audio_length_seconds })
            }
        ]);

        return data;
    } catch (error) {
        console.error("Error:", error);
        return null;
    }
};

export const logout = () => supabase.auth.signOut();

export const getWordDefinition = async ({
    language,
    word
}: {
    language: StudyLanguage;
    word: string;
}) => {
    const mapLanguageToTable: Record<StudyLanguage, string> = {
        "en-US": "en_dictionary",
        "es-ES": "es_dictionary",
        "it-IT": "it_dictionary",
        "de-DE": "de_dictionary",
        "pt-BR": "pt_dictionary"
    };

    // ensure that language is valid
    if (!Object.keys(mapLanguageToTable).includes(language)) return;

    try {
        const { data } = await supabase
            .from(mapLanguageToTable[language])
            .select("*")
            .eq("word", word)
            .maybeSingle();

        return data;
    } catch (error) {
        console.error("Error:", error);
        return null;
    }
};
